import React from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import * as R from "ramda";

import { SmallButtonBordered } from "components/Button";
import Icon from "components/Icon";
import Text from "components/Text";
import { InPersonPaymentInstructions } from "components/payments/InPersonPayment";
import Field from "components/project_form/Field";
import IssuedRequirementApplicationComponent from "components/projects/IssuedRequirementApplication";
import Fees from "components/projects/requirement/Fees";
import SkeuomorphicPaper from "components/utilities/SkeuomorphicPaper";
import { useActiveToggle } from "components/utilities/withActiveToggle";
import { useAnswerContext } from "contexts/answerContext";
import { useCurrentTenant } from "contexts/currentTenant";
import { useIsDeviceMobile } from "hooks/useDeviceType";
import { getRequirementSectionsList } from "queries/projects";
import { getSubmittedAt } from "reducers/requirementApplications";
import { isInPersonPayable } from "reducers/requirements";
import { formatDateStringAsDayFullMonth, now } from "utils/time";

import styles from "./Summary.scss";

const Section = ({ section }) => (
  <div className={styles.section}>
    <div className={styles.sectionHeader}>
      <div className={styles.sectionInfo}>
        <div className={styles.sectionTitle}>{section.name}</div>
      </div>
    </div>
    <div className={styles.sectionContent}>
      {R.map(
        (fieldIdent) => (
          <div className={styles.fieldContainer} key={fieldIdent}>
            <div className={styles.field}>
              <Field
                fieldIdentifier={fieldIdent}
                // changeType={renewalDiff[field.id]}
                behaviorType="disabled"
                hideRequired
                hideHelp
              />
            </div>
          </div>
        ),
        section.fieldIdents,
      )}
    </div>
  </div>
);
Section.propTypes = {
  section: PropTypes.object.isRequired,
};

const SubmittedRequirementApplication = ({
  children,
  application,
  requirement,
  renewalDiff = {}, // This is only injected from admin and used for Field#changeType
}) => {
  const { record: project } = useAnswerContext();
  let sections = getRequirementSectionsList(project, requirement);
  const { active, toggleActive } = useActiveToggle();
  const disclosureSection = R.last(sections);
  const submittedAt = getSubmittedAt(application);
  sections = R.dropLast(1, sections);
  const sectionsByColumn = R.splitAt(Math.round(sections.length / 2), sections);
  const { full_name: tenantFullName } = useCurrentTenant();
  const isMobile = useIsDeviceMobile();

  return (
    <div>
      <SkeuomorphicPaper>
        <div className={styles.requirement}>
          <h3 className={styles.requirementHeader}>
            <div>{tenantFullName}</div>
            <Text
              t={`projects.requirement.summary.requirement_name${
                requirement.type === "Upload" ? "_upload" : ""
              }`}
              name={requirement.name}
            />
            <div className={styles.requirementId}>#{application.id}</div>
          </h3>
          <div
            className={classnames(styles.requirementContent, {
              [styles.requirementContentDisabled]: isMobile && active,
            })}
          >
            {disclosureSection && (
              <div>
                <div className={styles.twoColumns}>
                  <div className={styles.sectionColumn}>
                    {R.map(
                      (section) => (
                        <Section
                          section={section}
                          key={section.id || section.key}
                          renewalDiff={renewalDiff}
                        />
                      ),
                      R.nth(0, sectionsByColumn),
                    )}
                  </div>
                  <div className={styles.sectionColumn}>
                    {R.map(
                      (section) => (
                        <Section
                          section={section}
                          key={section.id || section.key}
                          renewalDiff={renewalDiff}
                        />
                      ),
                      R.nth(1, sectionsByColumn),
                    )}
                  </div>
                </div>
                <div className={styles.disclosureSection}>
                  <Section
                    section={disclosureSection}
                    key={disclosureSection.id}
                    renewalDiff={renewalDiff}
                  />
                </div>
              </div>
            )}
            {children}
          </div>
          {isMobile && (
            <SmallButtonBordered onClick={toggleActive} className={styles.toggleButton}>
              <Icon icon={active ? "plus-square" : "minus-square"} />
              <span>
                <Text t={`projects.requirement.summary.${active ? "show" : "hide"}_content`} />
              </span>
            </SmallButtonBordered>
          )}
          <div className={styles.footerContainer}>
            {submittedAt && <SubmittedOn timestamp={submittedAt} />}
          </div>
        </div>
      </SkeuomorphicPaper>
    </div>
  );
};
SubmittedRequirementApplication.propTypes = {
  requirement: PropTypes.object.isRequired,
  application: PropTypes.object.isRequired,
};

const SubmittedOn = ({ timestamp }) => (
  <div className={styles.submittedOn}>
    <div className={styles.submittedOnLabel}>Submitted on</div>
    <div className={styles.submittedOnValue}>{formatDateStringAsDayFullMonth(timestamp)}</div>
  </div>
);
SubmittedOn.propTypes = { timestamp: PropTypes.string };

export const SubmittedSummary = ({ requirement, application }) => {
  const inPersonPayment = isInPersonPayable(requirement);
  const submittedAt = getSubmittedAt(application);

  return (
    <div>
      <div className={styles.printHeader}>Generated {formatDateStringAsDayFullMonth(now())}</div>
      <SubmittedRequirementApplication requirement={requirement} application={application}>
        <div className={styles.footerContainer}>
          {submittedAt && <SubmittedOn timestamp={submittedAt} />}
        </div>
        {requirement.payable && (
          <div className={styles.paymentsContainer}>
            <div className={styles.paymentsHeader}>
              <Text t="projects.requirement.summary.payments" />
            </div>
            <Fees requirementID={requirement.id} />
            {inPersonPayment && <InPersonPaymentInstructions requirement={requirement} />}
          </div>
        )}
      </SubmittedRequirementApplication>
    </div>
  );
};
SubmittedSummary.propTypes = {
  requirement: PropTypes.object.isRequired,
  application: PropTypes.object.isRequired,
};

const IssuedSummary = ({ projectID, requirement, requirementApplicationID }) => (
  <div>
    <div className={styles.printHeader}>Generated {formatDateStringAsDayFullMonth(now())}</div>
    <IssuedRequirementApplicationComponent
      projectID={projectID}
      requirement={requirement}
      requirementApplicationID={requirementApplicationID}
    />
  </div>
);
IssuedSummary.propTypes = {
  requirement: PropTypes.object.isRequired,
  requirementApplicationID: PropTypes.number.isRequired,
  projectID: PropTypes.number.isRequired,
};

const RequirementSummary = ({ requirement, application, state }) => {
  if (state === "issued" && !requirement.disable_issuance) return <IssuedSummary />;

  return <SubmittedSummary requirement={requirement} application={application} />;
};
RequirementSummary.propTypes = {
  state: PropTypes.string,
};

export default RequirementSummary;
